import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import FieldLabel from './FieldLabel';

const MAX_SHOTS = 6;

// The one Slack moment: moving a ticket to Validation. Asks for a note for the
// requestor and optional screenshots; both go to the ticket (thread +
// attachments) and into the Slack DM. Used by the board drop and the modal.
export default function ValidationDialog({ open, ticket, busy, onConfirm, onCancel }) {
  const [message, setMessage] = useState('');
  // { file, url } — url is a local preview, revoked when dropped / on close.
  const [shots, setShots] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setMessage('');
    setShots([]);
    return () => setShots((prev) => { prev.forEach((s) => URL.revokeObjectURL(s.url)); return []; });
  }, [open, ticket?.id]);

  if (!open || !ticket) return null;

  const addFiles = (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
    e.target.value = '';
    if (!files.length) return;
    setShots((prev) => [...prev, ...files.map((file) => ({ file, url: URL.createObjectURL(file) }))].slice(0, MAX_SHOTS));
  };
  const removeShot = (i) => setShots((prev) => {
    URL.revokeObjectURL(prev[i]?.url);
    return prev.filter((_, j) => j !== i);
  });

  const close = busy ? undefined : onCancel;
  return (
    <Dialog open={open} onClose={close} className="max-w-lg">
      <DialogHeader title="move-to-validation" onClose={close} />
      <DialogBody className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Moving <span className="font-medium text-foreground">#{ticket.id} {ticket.title}</span> to{' '}
          <span className="text-fuchsia-500">validation</span>. The requestor and assignees get a Slack DM with your
          note and screenshots — this is the only time Ticky messages them. The note is also posted on the ticket
          thread and the screenshots are attached to the ticket.
        </p>
        <div className="space-y-1.5">
          <FieldLabel htmlFor="v-msg">message for the requestor</FieldLabel>
          <Textarea
            id="v-msg"
            autoFocus
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. Please review the new pickup flow and confirm the labels look right."
            className="min-h-[120px]"
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <FieldLabel>screenshots</FieldLabel>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
              disabled={busy || shots.length >= MAX_SHOTS}
            >
              <ImagePlus className="h-3.5 w-3.5" /> add
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={addFiles} />
          </div>
          {shots.length ? (
            <div className="flex flex-wrap gap-2">
              {shots.map((s, i) => (
                <div key={s.url} className="group relative h-16 w-16 overflow-hidden rounded-md border border-border">
                  <img src={s.url} alt={s.file.name} className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeShot(i)}
                    className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition group-hover:opacity-100"
                    aria-label="Remove screenshot"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Optional — a picture of the result helps the requestor validate faster.</p>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button type="button" onClick={() => onConfirm({ message: message.trim(), images: shots.map((s) => s.file) })} disabled={busy}>
          {busy ? 'Moving…' : 'Move to validation & notify'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
